import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { API, authHeaders, formatStamp, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ConfirmDelete } from "./ConfirmDelete";

const MessageCard = ({ m, onDelete }) => (
  <li className="bg-white border border-[#E4DCF2] rounded-2xl p-5" data-testid={`admin-message-${m.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-extrabold text-lg" data-testid="admin-message-name">{m.name}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5E5672]">
          <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 hover:text-[#5A2D9C] break-all"><Mail className="w-4 h-4" />{m.email}</a>
          {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1 hover:text-[#5A2D9C]"><Phone className="w-4 h-4" />{m.phone}</a>}
        </div>
      </div>
      <ConfirmDelete title="Delete this message?" description="This permanently removes the message." onConfirm={() => onDelete(m.id)} testId={`admin-delete-message-${m.id}`} />
    </div>
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <Badge variant="outline" className="border-[#5A2D9C] text-[#5A2D9C]">{m.topic}</Badge>
      <span className="text-xs text-[#5E5672]">{formatStamp(m.created_at)}</span>
      <span className={`text-xs font-bold ${m.email_sent ? "text-[#1E9E5A]" : "text-[#D99A00]"}`}>{m.email_sent ? "Emailed" : "Saved (email not sent)"}</span>
    </div>
    <p className="mt-3 whitespace-pre-wrap text-[#1C1430]" data-testid="admin-message-body">{m.message}</p>
  </li>
);

export const MessagesTab = ({ onUnauthorized }) => {
  const [messages, setMessages] = useState(null);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) return onUnauthorized();
    toast.error(formatApiErrorDetail(err.response?.data?.detail));
  }, [onUnauthorized]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/messages`, { headers: authHeaders() }).then((r) => setMessages(r.data)).catch(handleErr);
  }, [handleErr]);

  useEffect(load, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders() });
      toast.success("Message deleted");
      load();
    } catch (err) { handleErr(err); }
  };

  return (
    <section data-testid="admin-messages-tab">
      <h2 className="font-display text-4xl">Messages</h2>
      <p className="text-[#5E5672] text-sm mb-6">Contact form submissions, newest first.</p>
      {messages === null && <p className="text-[#5E5672]">Loading…</p>}
      {messages?.length === 0 && <p className="text-[#5E5672]" data-testid="admin-no-messages">No messages yet.</p>}
      <ul className="grid gap-3" data-testid="admin-messages-list">
        {messages?.map((m) => <MessageCard key={m.id} m={m} onDelete={remove} />)}
      </ul>
    </section>
  );
};
